import React from 'react'

import isEmpty from 'lodash/isEmpty'
import PeopleIcon from '@material-ui/icons/People'
import BusinessIcon from '@material-ui/icons/Business'
import AssessmentIcon from '@material-ui/icons/Assessment'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'

import { saveToStorage } from 'reacticoon/storage'

import SecurityManager from 'modules/security/SecurityManager'
import useCadFactionChooser from 'components/useCadFactionChooser'
import { CAD_FACTION_STORAGE_KEY } from 'components/CadFactionContextDefinition'

// Fonction pour normaliser les accents
const normalizeString = (str) => {
  if (!str) return ''
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    maxWidth: 360,
    minWidth: 240,
    marginRight: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      padding: '0 !important',
      paddingRight: '32px !important',
    },
    '& .MuiAutocomplete-input': {
      padding: '6px 8px !important',
      fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },
  },
}))

export function LeftView() {
  const classes = useStyles()
  const { loggedInFaction, options, } = useCadFactionChooser()

  const selectedOption = options.find(opt => opt.value === loggedInFaction) || null
  const isDisabled = isEmpty(options) || (options.length === 1 && loggedInFaction === options[0]?.value)

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(event, newValue) => {
        if (newValue) {
          const factionName = typeof newValue === 'object' ? newValue.value : newValue
          // ⚠️ On écrit nous-mêmes dans le localStorage AVANT le reload.
          // `setSelectedCadFaction` vient de `useStorageState`, qui ne persiste que dans un
          // `useEffect` : sur une page lourde (le dispatch, l'onglet par défaut du CAD), le
          // re-render déclenché par le changement de faction retarde le flush des effets et la
          // navigation part avant l'écriture — la faction choisie était alors perdue au reload.
          saveToStorage(CAD_FACTION_STORAGE_KEY, factionName)
          window.location.reload()
        }
      }}
      options={options}
      getOptionLabel={(option) => {
        if (!option) return ''
        return option.label || option.value || ''
      }}
      getOptionSelected={(option, value) => {
        if (!option || !value) return false
        return option.value === value.value
      }}
      filterOptions={(opts, { inputValue }) => {
        const searchTerm = normalizeString(inputValue)
        return opts.filter(option => 
          normalizeString(option.label || '').includes(searchTerm) ||
          normalizeString(option.value || '').includes(searchTerm)
        )
      }}
      renderOption={(option) => option.label || option.value}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Choisir une faction..."
          variant="outlined"
          size="small"
        />
      )}
      disabled={isDisabled}
      noOptionsText="Aucune faction trouvée"
      className={classes.autocomplete}
    />
  )
}


// some params are unused but enforce the useContext to retrieve them, making the tabs reloaded
// on data changes
const createTabsConfigCad = (loggedInFaction, settings, userMe, cadUsefulData = {}) => () => {
  if (!settings) {
    return { tabs: [] }
  }

  // Une faction sans configuration de prime PPA/PDC ne fait pas de PPA/PDC :
  // on masque les onglets sessions et statistiques plutôt que d'appeler des
  // commandes qui répondront 404.
  // On ne masque que si l'API l'a explicitement indiqué (données chargées).
  const hasPPAConfig = cadUsefulData.hasPPAConfig !== false

  // Catégorie Citoyens
  const citoyensTabs = [
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeDashboard`)
      || SecurityManager.hasCadPermission(loggedInFaction, `playerCanSearchByName`) 
      || SecurityManager.hasCadPermission(loggedInFaction, `playerCanSearchByPhoneNumber`) 
      || SecurityManager.hasCadPermission(loggedInFaction, `plate`)
      || SecurityManager.hasCadPermission(loggedInFaction, `playerCanSearchByDna`)
      || SecurityManager.hasPermission('api.cad')
      ) && {
      label: 'Recherche citoyen',
      route: 'CAD_SEARCH',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `weaponSellCanViewSoldWeapons`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Ventes d\'armes',
      route: 'CAD_WEAPONS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeWantedPlayers`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Personnes recherchées',
      route: 'CAD_WANTED_PLAYERS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeWantedPlayers`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Bracelets éléctroniques',
      route: 'CAD_TRACKED_PLAYERS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `plate`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Véhicules recherchés',
      route: 'CAD_WANTED_VEHICLES',
    },
  ].filter(Boolean)

  // Catégorie Entreprise
  const entrepriseTabs = [
    (SecurityManager.hasCadPermission(loggedInFaction, ``) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Documents utiles',
      route: 'CAD_USEFUL_LINKS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `workforce`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Effectifs',
      route: 'CAD_WORKFORCE',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `factionVehicle`) || SecurityManager.hasPermission('api.cad')) && {
      label: "Véhicules",
      route: 'CAD_FACTION_VEHICLES',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `factionPet`) || SecurityManager.hasPermission('api.cad')) && {
      label: "Animaux",
      route: 'CAD_FACTION_PETS',
    },
    hasPPAConfig
      && (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeePPAStats`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Statistiques PPA/PDC',
      route: 'CAD_PPA_STATISTICS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `canSeeCriminalRecordStats`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Statistiques casiers judiciaires',
      route: 'CAD_CRIMINAL_RECORD_STATISTICS',
    },
  ].filter(Boolean)

  // Catégorie Opérationnel
  const operationnelTabs = [
    (SecurityManager.hasCadPermission(loggedInFaction, `dispatch`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Dispatch',
      route: 'CAD_DASHBOARD',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `operation`) || SecurityManager.hasPermission('api.cad.canViewOperations')) && {
      label: 'Opérations du jour',
      route: 'CAD_OPERATIONS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanViewCourtProceeding`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Procédures judiciaires',
      route: 'CAD_COURT_PROCEEDINGS',
    },
    hasPPAConfig
      && (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeePPA`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Sessions PPA/PDC',
      route: 'CAD_PPA',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeMedicalRecord`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Registre des décès',
      route: 'CAD_DEATH_NOTICES',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeMedicalRecord`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Certificats médicaux',
      route: 'CAD_CERTIFICATES',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeMedicalRecord`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Éval. psychotechniques',
      route: 'CAD_PSYCHO_EVAL',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeMedicalRecord`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Prescriptions',
      route: 'CAD_PRESCRIPTIONS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeeMedicalRecord`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Prises de sang',
      route: 'CAD_BLOOD_TESTS',
    },
    (SecurityManager.hasCadPermission(loggedInFaction, `playerCanSeePlayerMedicalFolders`) || SecurityManager.hasPermission('api.cad')) && {
      label: 'Donneurs volontaires',
      route: 'CAD_VOLUNTARY_DONORS',
    },
  ].filter(Boolean)

  return {
    leftView: () => <LeftView />,
    tabs: [
      // Catégorie Opérationnel (si au moins un sous-menu accessible)
      operationnelTabs.length > 0 && {
        label: 'Opérationnel',
        icon: <AssessmentIcon />,
        tabs: operationnelTabs,
      },
      
      // Catégorie Citoyens (si au moins un sous-menu accessible)
      citoyensTabs.length > 0 && {
        label: 'Citoyens',
        icon: <PeopleIcon />,
        tabs: citoyensTabs,
      },
      
      // Catégorie Entreprise (si au moins un sous-menu accessible)
      entrepriseTabs.length > 0 && {
        label: 'Entreprise',
        icon: <BusinessIcon />,
        tabs: entrepriseTabs,
      },
      
      // Permissions standalone
      (SecurityManager.hasCadPermission(loggedInFaction, `permission`) || SecurityManager.hasPermission('api.cad')) && 
      {
        label: 'Permissions',
        route: 'CAD_PERMISSIONS'
      }
    ].filter(Boolean),
  }

}

export default createTabsConfigCad
